import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../UserContext';
import { getRequests, updateRequestStatus, createBooking } from '../firebase';
import RequestsComponent from '../components/RequestsComponent';

const RequestsPage = () => {
  const { user, loading: userLoading } = useUser();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('inbound');

  const fetchRequests = useCallback(async () => {
    if (!user) {
      console.log('RequestsPage: User not loaded yet, waiting...');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      console.log('RequestsPage: Fetching requests for user:', user.uid);
      const fetchedRequests = await getRequests(user.uid);
      console.log('RequestsPage: Fetched requests:', fetchedRequests);
      setRequests(fetchedRequests);
    } catch (error) {
      console.error('RequestsPage: Error fetching data:', error);
      setError(`Failed to load data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchRequests();
    }
  }, [user, fetchRequests]);

  const handleStatusUpdate = async (requestId, newStatus) => {
    return new Promise(async (resolve, reject) => {
      try {
        console.log(`RequestsPage: Updating request ${requestId} status to ${newStatus}`);
        await updateRequestStatus(requestId, newStatus);
        if (newStatus === 'approved') {
          console.log(`RequestsPage: Creating booking for request ${requestId}`);
          await createBooking(requestId);
        }
        // Refresh the requests after updating
        await fetchRequests();
        resolve();
      } catch (error) {
        console.error('RequestsPage: Error updating request status:', error);
        reject(error);
      }
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    fetchRequests();
  };

  if (userLoading) {
    console.log('RequestsPage: Loading user data...');
    return <div>Loading user data...</div>;
  }

  if (!user) {
    console.log('RequestsPage: User not logged in');
    return <div>Please log in to view your requests.</div>;
  }

  const inboundRequests = requests.filter(request => request.type === 'inbound');
  const outboundRequests = requests.filter(request => request.type === 'outbound');

  console.log('RequestsPage: Inbound requests:', inboundRequests);
  console.log('RequestsPage: Outbound requests:', outboundRequests);

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Requests</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="mb-4">
        <nav className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('inbound')}
            className={`${
              activeTab === 'inbound'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            } px-3 py-2 font-medium text-sm rounded-md w-full sm:w-auto text-center`}
          >
            Inbound
          </button>
          <button
            onClick={() => handleTabChange('outbound')}
            className={`${
              activeTab === 'outbound'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-500 hover:text-gray-700'
            } px-3 py-2 font-medium text-sm rounded-md w-full sm:w-auto text-center`}
          >
            Outbound
          </button>
        </nav>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <RequestsComponent 
          requests={activeTab === 'inbound' ? inboundRequests : outboundRequests}
          handleStatusUpdate={handleStatusUpdate}
          type={activeTab === 'inbound' ? 'Inbound' : 'Outbound'}
        />
      )}

      {!loading && ((activeTab === 'inbound' && inboundRequests.length === 0) || (activeTab === 'outbound' && outboundRequests.length === 0)) && (
        <p className="text-center text-gray-500 mt-8">
          {activeTab === 'inbound' ? 'No requests received yet.' : 'No outbound requests found.'}
        </p>
      )}
    </div>
  );
};

export default RequestsPage;
